import { FC, MouseEvent, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";

import { EditIcon, VerticalEllipsisIcon } from "../../icons";
import { PinToggleIcon } from "../../icons/PinToggleIcon";

interface FileItemMenuProps {
  isPinned: boolean;
  onTogglePin: () => void | Promise<void>;
  onRename?: () => void;
  ariaLabel: string;
  /** Noun used in the menu items ("Pin file", "Rename letter"). */
  itemLabel?: string;
}

/** Per-row "..." menu for a file/letter entry in {@link SidebarFileViewer}: Pin/Unpin and Rename. */
const FileItemMenu: FC<FileItemMenuProps> = ({
  isPinned,
  onTogglePin,
  onRename,
  ariaLabel,
  itemLabel = "file",
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isPinning, setIsPinning] = useState(false);
  const menuOpen = Boolean(menuAnchor);

  const handleOpenMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuAnchor(e.currentTarget);
  };

  const handleCloseMenu = (event?: object) => {
    // MUI's Menu is portaled to document.body, but React re-bubbles its
    // synthetic events through the React tree, so a backdrop click here
    // would otherwise also trigger the row's onClick (select document).
    if (event && typeof (event as MouseEvent).stopPropagation === "function") {
      (event as MouseEvent).stopPropagation();
    }
    setMenuAnchor(null);
  };

  const handleTogglePin = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Blur the clicked menu item before closing: otherwise it can still hold
    // DOM focus when MUI marks the popover root aria-hidden during its exit
    // transition, which Chrome flags ("aria-hidden on focused descendant").
    (e.currentTarget as HTMLElement)?.blur();
    handleCloseMenu();
    setIsPinning(true);

    try {
      await onTogglePin();
    } finally {
      setIsPinning(false);
    }
  };

  const handleRename = (e: MouseEvent) => {
    if (!onRename) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();

    (e.currentTarget as HTMLElement)?.blur();
    handleCloseMenu();
    onRename();
  };

  return (
    <>
      {isPinning ? (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            flexShrink: 0,
          }}
        >
          <CircularProgress size={14} sx={{ color: "text.secondary" }} />
        </Box>
      ) : (
        <IconButton
          size="small"
          aria-label={ariaLabel}
          title="More options"
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={handleOpenMenu}
          className={`file-item-menu-button${menuOpen ? " menu-open" : ""}`}
          sx={{
            color: "text.secondary",
            flexShrink: 0,
            transition: "opacity 0.15s ease",
          }}
        >
          <VerticalEllipsisIcon className="w-4 h-4" />
        </IconButton>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
      >
        <MenuItem onClick={handleTogglePin} sx={{ gap: 1 }}>
          <ListItemIcon sx={{ minWidth: "0px !important" }}>
            <PinToggleIcon isPinned={isPinned} size={14} />
          </ListItemIcon>
          <ListItemText>{isPinned ? `Unpin ${itemLabel}` : `Pin ${itemLabel}`}</ListItemText>
        </MenuItem>

        {onRename ? (
          <MenuItem onClick={handleRename} sx={{ gap: 1 }}>
            <ListItemIcon sx={{ minWidth: "0px !important" }}>
              <EditIcon size={14} />
            </ListItemIcon>
            <ListItemText>Rename {itemLabel}</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

export default FileItemMenu;
